import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import { Autorenew as RefillIcon } from '@mui/icons-material';
import { getConfig } from '../services/config';
import { useAuth } from '../contexts/AuthContext';

interface RefillSettings {
  enabled: boolean;
  amount: number;
  cost_usd: number;
}

interface RefillSettingsCardProps {
  subscriptionId: string | null;
  initialSettings: RefillSettings | null;
  stripeCustomerId: string;
  onUpdate?: (settings: RefillSettings) => void;
  /** Shown instead of the controls when auto-refill can't be changed (e.g. no paid plan). */
  unavailableReason?: string;
  /** When the current billing period ends, for the "what happens when it's off" sentence. */
  periodEndLabel?: string;
}

const REFILL_AMOUNTS = [500, 1000, 1500, 2000, 2500, 3000, 5000];
const CREDIT_PRICE_USD = 0.01;

const formatUsd = (credits: number) => `$${(credits * CREDIT_PRICE_USD).toFixed(2)}`;

function describeRefill(enabled: boolean, amount: number, periodEndLabel?: string) {
  if (!enabled) {
    return `Auto-refill is off. When your credits run out, AI requests stop working until your plan renews${
      periodEndLabel ? ` on ${periodEndLabel}` : ''
    }.`;
  }
  return `When your credits run out, we automatically add credits in blocks of ${amount.toLocaleString()} (${formatUsd(
    amount,
  )} per block) and charge your card, so nothing stops working. Each refill covers what you've gone over plus one extra block.`;
}

export default function RefillSettingsCard({
  subscriptionId,
  initialSettings,
  stripeCustomerId,
  onUpdate,
  unavailableReason,
  periodEndLabel,
}: RefillSettingsCardProps) {
  const { user } = useAuth();
  const savedEnabled = initialSettings?.enabled ?? true;
  const savedAmount = initialSettings?.amount ?? 500;
  const [enabled, setEnabled] = useState(savedEnabled);
  const [amount, setAmount] = useState(savedAmount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setEnabled(savedEnabled);
    setAmount(savedAmount);
  }, [savedEnabled, savedAmount]);

  const isDirty = enabled !== savedEnabled || amount !== savedAmount;
  const amountOptions = REFILL_AMOUNTS.includes(savedAmount)
    ? REFILL_AMOUNTS
    : [...REFILL_AMOUNTS, savedAmount].sort((a, b) => a - b);
  const unavailable = !!unavailableReason;

  const handleDiscard = () => {
    setEnabled(savedEnabled);
    setAmount(savedAmount);
    setError(null);
  };

  const handleSave = async () => {
    if (!subscriptionId) {
      setError('No active subscription found');
      return;
    }

    if (!user) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const idToken = await user.getIdToken();
      
      const response = await fetch(
        `${getConfig().apiBaseUrl}/api/v2/billing/subscriptions/${subscriptionId}/update-refill-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            stripe_customer_id: stripeCustomerId,
            enabled,
            amount,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update refill settings');
      }

      const result = await response.json();
      setSuccess(true);

      if (onUpdate) {
        onUpdate({
          enabled: result.refill_settings.enabled,
          amount: result.refill_settings.amount,
          cost_usd: result.refill_settings.cost_usd,
        });
      }

      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update refill settings');

    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <RefillIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Auto-refill
          </Typography>
          {!unavailable && (
            <Chip
              size="small"
              label={savedEnabled ? 'On' : 'Off'}
              color={savedEnabled ? 'success' : 'default'}
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        {unavailable ? (
          <Typography variant="body2" color="text.secondary">
            {unavailableReason}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 760 }}>
              {describeRefill(enabled, amount, periodEndLabel)}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Auto-refill settings saved.
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={<Typography sx={{ fontWeight: 600 }}>Refill automatically</Typography>}
                sx={{ m: 0 }}
              />
              <FormControl size="small" sx={{ minWidth: 260 }} disabled={!enabled || loading}>
                <InputLabel id="refill-amount-label">Credits per refill block</InputLabel>
                <Select
                  labelId="refill-amount-label"
                  value={amount}
                  label="Credits per refill block"
                  onChange={(e) => setAmount(e.target.value as number)}
                >
                  {amountOptions.map((amt) => (
                    <MenuItem key={amt} value={amt}>
                      {amt.toLocaleString()} credits ({formatUsd(amt)})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isDirty && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
                  >
                    {loading ? 'Saving...' : 'Save changes'}
                  </Button>
                  <Button onClick={handleDiscard} disabled={loading}>
                    Discard
                  </Button>
                </Box>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Credits cost $0.01 each. Refill blocks come in multiples of 500 credits.
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}
